import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Navigate } from "react-router-dom";
import axios from 'axios';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import LoggedLayout from "layouts/LoggedLayout";
import { getCommunity } from 'helpers/community';
import { setToSession } from 'helpers/session';
import Skeleton from 'react-loading-skeleton';
import Paginator from 'components/Paginator';
import moment from 'moment';
import CloseFiscalYearModal from './CloseFiscalYearModal';

const OPERATION_LABELS = {
	opening: 'Abriendo ejercicio…',
	copying_balances: 'Copiando saldos iniciales…',
	recalculating: 'Recalculando ejercicio…',
};

const OPERATION_SUCCESS_MSG = {
	opening: 'Ejercicio abierto correctamente',
	copying_balances: 'Saldos iniciales copiados correctamente',
	recalculating: 'Ejercicio recalculado correctamente',
};

const FinancialYear = styled.div`
	background: white;
	border-radius: 10px;
	margin-bottom: 20px;
	text-align: center;
	color: var(--bs-gray-700);
	padding: 20px;
	border: 1px solid var(--bs-gray-200);
	position: relative;
	min-height: 180px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: stretch;

	${props => props.$new ? 'cursor: pointer; background: var(--bs-primary); color: white; border: 0; justify-content: center; font-size: 25px;' : ''}

	.year-number {
		font-size: 32px;
		font-weight: 600;
		line-height: 1;
		margin-bottom: 8px;
	}

	.status-badge {
		margin-bottom: 12px;
	}

	.actions {
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		gap: 6px;
		justify-content: center;

		.btn {
			flex: 1 1 0;
			min-width: 0;
			max-width: 33%;
			white-space: normal;
			word-break: break-word;
			line-height: 1.2;
		}

		@media (max-width: 576px) {
			flex-direction: column;

			.btn {
				flex: 1 1 auto;
			}
		}
	}

	.delete-btn {
		position: absolute;
		top: -10px;
		right: -10px;
		background: var(--bs-danger);
		color: white;
		border-radius: 50%;
		width: 24px;
		height: 24px;
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: 14px;
		border: 0;
	}

	.operation-overlay {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 10px;
		padding: 20px 0;

		.spinner-border {
			width: 2rem;
			height: 2rem;
			color: var(--bs-primary);
		}

		.operation-label {
			font-size: 14px;
			color: var(--bs-gray-700);
			font-weight: 500;
		}
	}
`;

let axiosCancelToken = null;

export default function FinancialYears() {
	const [queryParams] = useSearchParams();

	let [forceReload, setForceReload] = useState(null);
	let [financialYears, setFinancialYears] = useState(undefined);
	let [busyId, setBusyId] = useState(null);
	let [closeModalFor, setCloseModalFor] = useState(null);
	let [page, _setPage] = useState(queryParams.get('page') ?? undefined);
	const setPage = (page) => {
		setFinancialYears({...financialYears});
		_setPage(page);
	}

	// Track previous operation state per FY so we can toast when an operation
	// transitions from "in-flight" to "done/error" during polling.
	const prevOperationsRef = useRef({});

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();

		return function cleanup() {
			axiosCancelToken.cancel();
		}
	}, []);

	const loadList = async (silent = false) => {
		if ( !silent ) setFinancialYears(undefined);
		try {
			const response = await axios.get('/api/manager/financial-years/list', {
				params: { community_id: getCommunity()?.id },
				cancelToken: axiosCancelToken.token,
			});
			const list = response.data;

			// Detect transitions: if a FY had an operation in the previous snapshot
			// but doesn't now, the Job finished → toast.
			const prev = prevOperationsRef.current;
			const next = {};
			(list.data ?? []).forEach((fy) => {
				const opNow = fy.current_operation || null;
				const errNow = fy.operation_error || null;
				const opBefore = prev[fy.id]?.operation ?? null;

				if ( opBefore && !opNow ) {
					// Operation just finished
					if ( errNow ) {
						toast.error('Error: ' + errNow);
					} else {
						toast.success(OPERATION_SUCCESS_MSG[opBefore] ?? 'Operación completada');

						// Auto-select the year when an "opening" job finishes, as if
						// the user clicked the "Seleccionar" button. Only on opening,
						// NOT on copying_balances (which doesn't change the meaningful
						// selection). Fire-and-forget; errors are non-fatal.
						if ( opBefore === 'opening' ) {
							axios.post('/api/manager/financial-years/select/' + fy.id, {
								community_id: getCommunity()?.id,
							}, { cancelToken: axiosCancelToken.token })
								.then((response) => {
									const year = response?.data?.year ?? fy.year;
									const status = fy.status ?? 'open';
									setToSession('navbar-fiscalyear', year);
									setToSession('navbar-fiscalyear-status', status);
									document.dispatchEvent(new CustomEvent('fiscalYearChanged', { detail: { year, status } }));
									toast.info('Ejercicio ' + year + ' seleccionado');
								})
								.catch(() => {
									// Fall back: still emit the change event so the navbar refreshes.
									document.dispatchEvent(new CustomEvent('fiscalYearChanged'));
								});
						} else {
							document.dispatchEvent(new CustomEvent('fiscalYearChanged'));
						}
					}
				}

				next[fy.id] = { operation: opNow };
			});
			prevOperationsRef.current = next;

			setFinancialYears({...list});
		} catch (error) {
			if ( axios.isCancel(error) ) return;
		}
	};

	useEffect(() => {
		loadList(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, forceReload]);

	// Polling while any FY has an active async operation.
	useEffect(() => {
		const data = financialYears?.data ?? [];
		const anyActive = data.some(fy => !!fy.current_operation);
		if ( !anyActive ) return;

		const interval = setInterval(() => loadList(true), 2000);
		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [financialYears]);

	const reloadAndNotify = () => {
		setForceReload(Math.random());
		setPage(1);
		document.dispatchEvent(new CustomEvent('fiscalYearChanged'));
	}

	const handleError = (error) => {
		if ( axios.isCancel(error) ) return;
		const msg = error?.response?.data?.message ?? 'Se ha producido un error.';
		toast.error(msg);
	}

	const newFinancialYear = () => {
		const c = window.confirm('¿Seguro que quieres abrir un nuevo ejercicio fiscal?');
		if ( !c ) return false;

		setBusyId('new');
		axios.post('/api/manager/financial-years/add', {
			community_id: getCommunity()?.id,
		}, {
			cancelToken: axiosCancelToken.token
		}).then(() => {
			reloadAndNotify();
		}).catch(handleError).finally(() => setBusyId(null));
	}

	const deleteFinancialYear = (id) => {
		const c = window.confirm('¿Seguro que quieres eliminar este ejercicio fiscal?');
		if ( !c ) return false;

		setBusyId(id);
		axios.post('/api/manager/financial-years/delete/' + id, {
			community_id: getCommunity()?.id,
		}, {
			cancelToken: axiosCancelToken.token
		}).then(() => {
			reloadAndNotify();
		}).catch(handleError).finally(() => setBusyId(null));
	}

	const openCloseModal = (fy) => {
		const data = financialYears?.data ?? [];
		const nextYear = data.find(el => el.year === fy.year + 1) ?? null;
		setCloseModalFor({ fy, nextYear });
	}

	const confirmCloseFinancialYear = (copyBalances) => {
		if ( !closeModalFor ) return;
		const fy = closeModalFor.fy;
		const nextYear = closeModalFor.nextYear;

		setBusyId(fy.id);
		axios.post('/api/manager/financial-years/close/' + fy.id, {
			community_id: getCommunity()?.id,
			copy_balances_to_next: copyBalances ? 1 : 0,
		}, {
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			const copyStatus = response?.data?.copy_status;
			if ( copyStatus === 'queued' ) {
				toast.success('Ejercicio ' + fy.year + ' cerrado. Copiando saldos al ejercicio ' + (nextYear?.year ?? '') + '…');
			} else if ( copyStatus === 'busy' ) {
				toast.warning('Ejercicio cerrado, pero hay otra operación en curso en el ejercicio ' + (nextYear?.year ?? '') + '. Copia los saldos manualmente cuando termine.');
			} else {
				toast.success('Ejercicio ' + fy.year + ' cerrado');
			}
			reloadAndNotify();
		}).catch(handleError).finally(() => {
			setBusyId(null);
			setCloseModalFor(null);
		});
	}

	const reopenFinancialYear = (id) => {
		const c = window.confirm('¿Seguro que quieres REABRIR este ejercicio fiscal?');
		if ( !c ) return false;

		setBusyId(id);
		axios.post('/api/manager/financial-years/reopen/' + id, {
			community_id: getCommunity()?.id,
		}, {
			cancelToken: axiosCancelToken.token
		}).then(() => {
			reloadAndNotify();
		}).catch(handleError).finally(() => setBusyId(null));
	}

	const selectFinancialYear = (el) => {
		setBusyId(el.id);
		axios.post('/api/manager/financial-years/select/' + el.id, {
			community_id: getCommunity()?.id,
		}, {
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			const year = response?.data?.year ?? el.year;
			const status = el.status ?? null;
			setToSession('navbar-fiscalyear', year);
			setToSession('navbar-fiscalyear-status', status);
			// El detalle debe incluir el estado: ClosedFiscalYearBanner lo lee del evento
			// y sin él ocultaría el aviso al seleccionar un ejercicio cerrado.
			document.dispatchEvent(new CustomEvent('fiscalYearChanged', { detail: { year, status } }));
			toast.success('Ejercicio ' + year + ' seleccionado');
		}).catch(handleError).finally(() => setBusyId(null));
	}

	const copyInitialBalances = (id) => {
		const c = window.confirm('¿Seguro que quieres copiar los saldos iniciales desde el ejercicio anterior? Esta operación sobreescribe el asiento de apertura.');
		if ( !c ) return false;

		setBusyId(id);
		axios.post('/api/manager/financial-years/copy-initial-balances/' + id, {
			community_id: getCommunity()?.id,
		}, {
			cancelToken: axiosCancelToken.token
		}).then(() => {
			reloadAndNotify();
		}).catch(handleError).finally(() => setBusyId(null));
	}

	const recalculateFinancialYear = (id) => {
		const c = window.confirm('¿Seguro que quieres recalcular este ejercicio cerrado? Se regenerarán los asientos contables que falten.');
		if ( !c ) return false;

		setBusyId(id);
		axios.post('/api/manager/financial-years/recalculate/' + id, {
			community_id: getCommunity()?.id,
		}, {
			cancelToken: axiosCancelToken.token
		}).then(() => {
			reloadAndNotify();
		}).catch(handleError).finally(() => setBusyId(null));
	}

	if ( !getCommunity()?.id ) return <Navigate to="/" />;

	const data = financialYears?.data ?? [];
	const latestYear = data[0]?.year ?? 0;
	const latestIsClosed = data[0]?.status === 'closed';
	const canAddNew = latestIsClosed && latestYear < parseInt(moment().format('YYYY')) + 1;

	return (
		<LoggedLayout>
			<section>
				<div className="page-title">
					<h1>Ejercicios fiscales</h1>
				</div>

				<div className="page-content">
					<div className="row">
						{ canAddNew &&
							<div className="col-md-4">
								<FinancialYear $new={true} onClick={newFinancialYear}>
									<div>
										<i className="bi bi-plus"></i> Abrir nuevo ejercicio
									</div>
								</FinancialYear>
							</div>
						}

						{ data.length > 0 &&
							data.map((el, idx) => {
								const isBusy = busyId === el.id;
								const isOpen = el.status === 'open';
								const hasOperation = !!el.current_operation;
								const hasPrevYear = data.some(other => other.year === el.year - 1);

								return (
									<div key={idx} className="col-md-4">
										<FinancialYear>
											{ hasOperation ? (
												<>
													<div>
														<div className="year-number">{el.year}</div>
													</div>
													<div className="operation-overlay">
														<div className="spinner-border" role="status">
															<span className="visually-hidden">Cargando…</span>
														</div>
														<div className="operation-label">
															{OPERATION_LABELS[el.current_operation] ?? 'Procesando…'}
														</div>
													</div>
												</>
											) : (
												<>
													{ el.is_deletable &&
														<button
															className="delete-btn"
															disabled={isBusy}
															onClick={() => deleteFinancialYear(el.id)}
															title="Eliminar ejercicio"
														>
															<i className="bi bi-x"></i>
														</button>
													}

													<div>
														<div className="year-number">{el.year}</div>
														<div className="status-badge">
															<span className={'badge ' + (isOpen ? 'bg-primary' : 'bg-secondary')}>
																{isOpen ? 'Abierto' : 'Cerrado'}
															</span>
														</div>
													</div>

													<div className="actions">
														<button
															type="button"
															className="btn btn-sm btn-outline-primary"
															disabled={isBusy}
															onClick={() => selectFinancialYear(el)}
														>
															<i className="bi bi-check2-circle"></i> Seleccionar
														</button>

														{ el.is_closable &&
															<button
																type="button"
																className="btn btn-sm btn-outline-secondary"
																disabled={isBusy}
																onClick={() => openCloseModal(el)}
															>
																<i className="bi bi-lock"></i> Cerrar
															</button>
														}

														{ el.is_reopenable &&
															<button
																type="button"
																className="btn btn-sm btn-outline-success"
																disabled={isBusy}
																onClick={() => reopenFinancialYear(el.id)}
															>
																<i className="bi bi-unlock"></i> Reabrir
															</button>
														}

														{ isOpen && hasPrevYear &&
															<button
																type="button"
																className="btn btn-sm btn-outline-info"
																disabled={isBusy}
																onClick={() => copyInitialBalances(el.id)}
																title="Sobreescribe el asiento de apertura con los saldos finales del ejercicio anterior"
															>
																<i className="bi bi-arrow-down-circle"></i> Copiar saldos iniciales
															</button>
														}

														{/* Botón Recalcular oculto por ahora
														{ !isOpen &&
															<button
																type="button"
																className="btn btn-sm btn-outline-warning"
																disabled={isBusy}
																onClick={() => recalculateFinancialYear(el.id)}
																title="Regenera los asientos contables que falten en este ejercicio cerrado"
															>
																<i className="bi bi-arrow-clockwise"></i> Recalcular
															</button>
														}
														*/}
													</div>
												</>
											) }
										</FinancialYear>
									</div>
								);
							})
						}

						{ financialYears && !data.length && <div>No hay datos disponibles</div> }

						{ financialYears === undefined &&
							[1,2,3].map((el, idx) => {
								return (
									<div key={idx} className="col-md-4">
										<Skeleton height={180} />
									</div>
								);
							})
						}

						<div className="col-md-12 text-center">
							<div className="d-inline-block">
								<Paginator
									min={1}
									current={financialYears?.current_page}
									max={financialYears?.last_page}
									changeCallback={(page) => setPage(page)}
								/>
							</div>
						</div>
					</div>
				</div>
			</section>

			{ closeModalFor &&
				<CloseFiscalYearModal
					financialYear={closeModalFor.fy}
					nextYear={closeModalFor.nextYear}
					nextYearHasOpeningEntry={!!closeModalFor.nextYear?.has_opening_entry}
					onConfirm={confirmCloseFinancialYear}
					onClose={() => setCloseModalFor(null)}
				/>
			}
		</LoggedLayout>
	);
}
